import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { UserPlus, Newspaper, Briefcase } from 'lucide-react';

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: role, 2: form
  const [playerRole, setPlayerRole] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [gender, setGender] = useState('male');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    setLoading(true);
    setError('');
    const result = await register(email, password, name, gender, playerRole);
    setLoading(false);
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center p-4 relative overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center opacity-15 mix-blend-screen"
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1563277267-0e9f2dabb9ba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwyfHxyZXRybyUyMGFyY2FkZSUyMG1hY2hpbmV8ZW58MHx8fHwxNzc2NDMxOTY5fDA&ixlib=rb-4.1.0&q=85)' }}
      />

      <div className="relative z-10 w-full max-w-sm">
        {/* Step 1: Role Selection */}
        {step === 1 && (
          <div>
            <div className="text-center mb-8">
              <h1 className="font-heading text-4xl sm:text-5xl uppercase tracking-widest text-[#E61C24] drop-shadow-[0_0_15px_rgba(230,28,36,0.8)]">
                CHOOSE YOUR CLASS
              </h1>
              <p className="font-mono text-sm text-[#A1A1AA] mt-2">Quel est votre métier ?</p>
            </div>

            <div className="space-y-4">
              <button
                data-testid="role-presse-btn"
                onClick={() => { setPlayerRole('presse'); setStep(2); }}
                className="w-full bg-[#121214] border-2 border-[#00AEEF]/50 hover:border-[#00AEEF] p-6 text-left transition-all hover:shadow-[0_0_20px_rgba(0,174,239,0.3)] group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-[#00AEEF]/20 border-2 border-[#00AEEF]/50 flex items-center justify-center group-hover:bg-[#00AEEF]/30">
                    <Newspaper className="text-[#00AEEF]" size={28} />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl uppercase tracking-wider text-[#00AEEF] group-hover:text-[#33C3FF]">
                      Attaché(e) de Presse
                    </h3>
                    <p className="font-body text-sm text-[#71717A] mt-1">
                      Pitchez vos sujets aux journalistes, passez les barrages
                    </p>
                  </div>
                </div>
              </button>

              <button
                data-testid="role-commercial-btn"
                onClick={() => { setPlayerRole('commercial'); setStep(2); }}
                className="w-full bg-[#121214] border-2 border-[#E61C24]/50 hover:border-[#E61C24] p-6 text-left transition-all hover:shadow-[0_0_20px_rgba(230,28,36,0.3)] group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-[#E61C24]/20 border-2 border-[#E61C24]/50 flex items-center justify-center group-hover:bg-[#E61C24]/30">
                    <Briefcase className="text-[#E61C24]" size={28} />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl uppercase tracking-wider text-[#E61C24] group-hover:text-[#FF333B]">
                      Commercial(e)
                    </h3>
                    <p className="font-body text-sm text-[#71717A] mt-1">
                      Convainquez prospects et décideurs, closez en 90 secondes
                    </p>
                  </div>
                </div>
              </button>
            </div>

            <div className="mt-6 text-center">
              <Link to="/login" data-testid="login-link" className="font-mono text-[#A1A1AA] hover:text-white text-sm underline transition-colors">
                DEJA INSCRIT ? CONNEXION
              </Link>
            </div>
          </div>
        )}

        {/* Step 2: Registration Form */}
        {step === 2 && (
          <div>
            <div className="text-center mb-6">
              <h1 className="font-heading text-3xl uppercase tracking-widest text-[#E61C24] drop-shadow-[0_0_10px_rgba(230,28,36,0.6)]">
                PLAYER 1
              </h1>
              <p className="font-mono text-sm text-[#FFF200] mt-1">
                {playerRole === 'presse' ? 'ATTACHE(E) DE PRESSE' : 'COMMERCIAL(E)'}
              </p>
            </div>

            <div className="bg-[#121214] border-2 border-[#00AEEF]/50 shadow-[inset_0_0_20px_rgba(0,174,239,0.15)] p-5">
              <div className="flex items-center gap-2 mb-4">
                <UserPlus className="text-[#00AEEF]" size={20} />
                <h2 className="font-heading text-lg uppercase tracking-wider text-white">Inscription</h2>
              </div>

              {error && (
                <div data-testid="register-error" className="bg-[#E61C24]/20 border border-[#E61C24] text-[#FF333B] font-mono text-sm p-3 mb-4">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-3">
                <div>
                  <label className="block font-mono text-[#00AEEF] text-xs mb-1 uppercase">Nom / Pseudo</label>
                  <input
                    data-testid="register-name-input"
                    type="text"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    className="bg-black border-2 border-[#00AEEF]/50 text-white font-mono text-base p-3 w-full focus:border-[#00AEEF] focus:outline-none transition-all rounded-none"
                    placeholder="Votre nom"
                    required
                  />
                </div>
                <div>
                  <label className="block font-mono text-[#00AEEF] text-xs mb-1 uppercase">Email</label>
                  <input
                    data-testid="register-email-input"
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    className="bg-black border-2 border-[#00AEEF]/50 text-white font-mono text-base p-3 w-full focus:border-[#00AEEF] focus:outline-none transition-all rounded-none"
                    placeholder="your@email.com"
                    required
                  />
                </div>
                <div>
                  <label className="block font-mono text-[#00AEEF] text-xs mb-1 uppercase">Mot de passe</label>
                  <input
                    data-testid="register-password-input"
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className="bg-black border-2 border-[#00AEEF]/50 text-white font-mono text-base p-3 w-full focus:border-[#00AEEF] focus:outline-none transition-all rounded-none"
                    placeholder="Min 6 caractères"
                    required
                  />
                </div>
                <div>
                  <label className="block font-mono text-[#00AEEF] text-xs mb-1 uppercase">Genre (pour la voix IA)</label>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      data-testid="gender-male-btn"
                      onClick={() => setGender('male')}
                      className={`flex-1 py-2.5 font-heading text-base uppercase tracking-wider border-2 transition-all ${
                        gender === 'male'
                          ? 'bg-[#00AEEF] text-black border-[#00AEEF]'
                          : 'bg-transparent text-[#00AEEF] border-[#00AEEF]/30'
                      }`}
                    >
                      Homme
                    </button>
                    <button
                      type="button"
                      data-testid="gender-female-btn"
                      onClick={() => setGender('female')}
                      className={`flex-1 py-2.5 font-heading text-base uppercase tracking-wider border-2 transition-all ${
                        gender === 'female'
                          ? 'bg-[#E61C24] text-white border-[#E61C24]'
                          : 'bg-transparent text-[#E61C24] border-[#E61C24]/30'
                      }`}
                    >
                      Femme
                    </button>
                  </div>
                </div>
                <button
                  data-testid="register-submit-btn"
                  type="submit"
                  disabled={loading}
                  className="bg-[#E61C24] hover:bg-[#FF333B] text-white font-heading text-lg uppercase tracking-widest py-3 px-8 border-b-4 border-red-900 active:border-b-0 active:translate-y-1 transition-all shadow-[0_0_15px_rgba(230,28,36,0.6)] w-full text-center disabled:opacity-50"
                >
                  {loading ? 'CREATING...' : 'GO!'}
                </button>
              </form>

              <button
                data-testid="back-role-btn"
                onClick={() => setStep(1)}
                className="w-full mt-3 font-mono text-xs text-[#71717A] hover:text-white transition-colors text-center"
              >
                &larr; Changer de rôle
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
